/**
 * billingApi.js — Billing endpoints (account, plans, checkout, portal).
 *
 * All calls go through apiClient so auth + 401 refresh are handled uniformly.
 * Responses are Axios responses — read payloads via `.data`.
 *
 * Backend contract (see backend/src/api/routers/billing_router.py):
 *   GET  /api/v1/billing/account  → {account_id, plan_type, status, credit_balance, ...}
 *   GET  /api/v1/billing/plans    → {seat_monthly, seat_annual, signup_bonus_credits, publishable_key}
 *   POST /api/v1/billing/checkout → {url, session_id}
 *   POST /api/v1/billing/portal   → {url}
 *
 * When backend BILLING_ENABLED=false the account endpoint still returns a
 * neutral state (balance=signup_bonus_credits, billing_enabled=false). The
 * frontend uses that flag to decide whether to render paywall UI at all.
 */
import client from './apiClient';

const BASE = '/api/v1/billing';

const billingApi = {
  /** Fetch the current user's billing account. Auto-creates on first call. */
  getAccount: () => client.get(`${BASE}/account`),

  /** Public pricing info for the checkout page. No auth required server-side,
   *  but we still go through client so the token is attached if present. */
  getPlans: () => client.get(`${BASE}/plans`),

  /**
   * Create a Stripe Checkout Session and return its URL + session_id.
   *
   * @param {Object} body
   * @param {'payg_monthly'|'annual'} body.plan
   * @param {number} [body.seat_count=1]
   * @param {string} body.successUrl — browser redirect on success
   * @param {string} body.cancelUrl — browser redirect on cancel
   */
  createCheckoutSession: ({ plan, seat_count = 1, successUrl, cancelUrl }) =>
    client.post(`${BASE}/checkout`, {
      plan,
      seat_count,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }),

  /** Stripe-hosted Customer Portal (card, invoices, cancel). */
  createPortalSession: (returnUrl) =>
    client.post(`${BASE}/portal`, null, { params: { return_url: returnUrl } }),
};

export default billingApi;
